import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Section, SectionHeader } from "@/components/shared/Section";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Shield, MapPin, Star, ArrowRight, Ruler, Calendar, Eye, Image } from "lucide-react";
import { motion } from "framer-motion";
import { useCompany } from "@/contexts/BusinessSettingsContext";
import { supabase } from "@/integrations/supabase/client";
import { Link as RouterLink } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { cdnImage } from "@/lib/image-cdn";
import ReviewsJsonLd from "@/components/ReviewsJsonLd";
import heroSlideKitchen from "@/assets/hero-slide-kitchen.jpg.asset.json";

interface HomeMaterial {
  id: string;
  slug?: string | null;
  name: string;
  description: string | null;
  image_url: string | null;
  category: string;
}

const fallbackMaterials: HomeMaterial[] = [
  { id: "granite", name: "Granite", description: "Timeless durability with natural beauty", image_url: null, category: "granite" },
  { id: "quartz", name: "Quartz", description: "Engineered elegance with low maintenance", image_url: null, category: "quartz" },
  { id: "quartzite", name: "Quartzite", description: "Natural beauty with high durability and unique movement", image_url: null, category: "quartzite" },
  { id: "marble", name: "Marble", description: "Classic luxury for refined spaces", image_url: null, category: "marble" },
];

const processSteps = [
  { icon: Eye, title: "Browse & Select", desc: "Explore our curated collection of premium stone slabs", cta: "Browse Materials", href: "/materials", highlight: false },
  { icon: Ruler, title: "Get Your Estimate", desc: "Receive an estimated investment range — no commitment required", cta: "Start My Estimate", href: "/quote", highlight: true },
  { icon: Calendar, title: "Schedule Consultation", desc: "Book a free in-home measurement with our experts", cta: "Book Free Consultation", href: "/book", highlight: false },
  { icon: Image, title: "Relax & Enjoy", desc: "Professional fabrication and installation, fully insured", cta: "View Our Work", href: "/gallery", highlight: false },
];

const Index = () => {
  useSEO({
    title: "Altar Stone | Countertop Installation Sarasota FL",
    description: "Premium quartz, marble, quartzite & granite countertops in Sarasota and Southwest Florida. Free online quote. Call (941) 541-3348.",
    canonical: "/",
  });
  const navigate = useNavigate();
  const co = useCompany();
  const [homeMaterials, setHomeMaterials] = useState<HomeMaterial[]>(fallbackMaterials);
  const [featuredProjects, setFeaturedProjects] = useState<any[]>([]);
  const [heroSlides, setHeroSlides] = useState<{ url: string; alt: string }[]>([
    { url: heroSlideKitchen.url, alt: "Luxury white marble kitchen island with waterfall countertop" },
  ]);
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    supabase
      .from("materials")
      .select("id, slug, name, description, image_url, category, show_on_home")
      .eq("is_active", true)
      .order("display_order")
      .then(({ data }: { data: any[] | null }) => {
        const homeVisible = (data || []).filter((m: any) => m.show_on_home);
        if (homeVisible.length > 0) setHomeMaterials(homeVisible);
        else if (data && data.length > 0) setHomeMaterials(data.slice(0, 4));
      });

    // Fetch featured projects
    supabase
      .from("projects")
      .select("*")
      .eq("status", "published")
      .order("created_at", { ascending: false })
      .limit(6)
      .then(async ({ data: pData }) => {
        if (pData && pData.length > 0) {
          const { data: iData } = await supabase
            .from("project_images")
            .select("*")
            .in("project_id", (pData as any[]).map((p: any) => p.id))
            .order("display_order");
          const imgMap: Record<string, any> = {};
          (iData as any[] || []).forEach((img: any) => { if (!imgMap[img.project_id]) imgMap[img.project_id] = img; });
          const projectSlides = (pData as any[])
            .map((p: any) => imgMap[p.id])
            .filter(Boolean)
            .slice(0, 3)
            .map((img: any) => ({ url: img.image_url, alt: img.alt_text || "Countertop project by Altar Stone" }));
          if (projectSlides.length > 0) {
            setHeroSlides((prev) => [...prev, ...projectSlides]);
          }
          setFeaturedProjects((pData as any[]).map((p: any) => ({ ...p, thumb: imgMap[p.id] })));
        }
      });
  }, []);

  // Auto-advance the hero background slideshow
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const t = setInterval(() => setHeroIndex((i) => (i + 1) % heroSlides.length), 6000);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  const faqs = [
    { q: "How much do countertops cost in Sarasota Florida?", a: "Countertop costs in Sarasota range from $60 to $150+ per square foot installed depending on material. Quartz countertops typically cost $70–$120/sqft, marble $80–$150/sqft, and granite $60–$100/sqft. Contact Altar Stone at (941) 541-3348 for a free quote." },
    { q: "What areas do you serve?", a: "We serve all of Southwest Florida including Sarasota, Bradenton, Venice, Port Charlotte, Punta Gorda, North Port, Fort Myers, Naples, Cape Coral, Bonita Springs, Englewood and surrounding areas." },
    { q: "How long does countertop installation take in Sarasota?", a: "Most kitchen countertop installations in Sarasota are completed in 1–2 days. The full process from estimate to installation typically takes 1–2 weeks." },
    { q: "What is the best countertop for Florida homes?", a: "Quartz and quartzite are excellent choices for Florida homes. Quartz is non-porous and resists humidity and staining. Quartzite is UV-stable, ideal for outdoor kitchens in Florida's sunny climate." },
    { q: "Do you offer free estimates in Sarasota?", a: "Yes! Get a free online estimate at countertopsaltarstone.com or call us at (941) 541-3348 to schedule a free in-home measurement anywhere in Southwest Florida." },
  ];

  const handleStepClick = (href: string) => {
    if (href === "/book") {
      const draft = localStorage.getItem("estimator_draft_v1");
      if (draft) {
        try {
          const parsed = JSON.parse(draft);
          if (parsed.leadId) {
            navigate("/quote", { state: { jumpToSchedule: true } });
            return;
          }
        } catch { /* ignore */ }
      }
      navigate("/quote");
      return;
    }
    navigate(href);
  };

  return (
    <>
      <ReviewsJsonLd />
      {/* Hero */}
      <section className="relative bg-primary text-primary-foreground overflow-hidden">
        {/* Background slideshow */}
        <div className="absolute inset-0">
          {heroSlides.map((slide, i) => (
            <img
              key={slide.url}
              src={cdnImage(slide.url, { width: 1600, quality: 75 })}
              alt={slide.alt}
              width="1600"
              height="1000"
              aria-hidden={i !== heroIndex}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ease-in-out ${i === heroIndex ? "opacity-100" : "opacity-0"}`}
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : undefined}
              onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
            />
          ))}
          {/* Dark tint + left gradient so the headline stays readable */}
          <div className="absolute inset-0 bg-primary/50" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/70 to-primary/20" />
        </div>
        <div className="container relative py-24 md:py-36 lg:py-44">
          <div className="max-w-2xl">
            <p className="text-accent font-medium text-sm uppercase tracking-widest mb-4">
              Premium Countertops
            </p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Transform Your Space with Natural Stone
            </h1>
            <p className="text-primary-foreground/90 text-lg md:text-xl mb-8 max-w-lg">
              Hand-selected granite, quartz, and marble — expertly fabricated and installed by licensed professionals.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-base">
                <Link to="/quote">Get Your Estimate <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/slabs">Browse Slabs</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>


      {/* Featured Materials */}
      <Section>
        <SectionHeader title="Our Materials" subtitle="Choose from the finest natural and engineered stone for your project" />
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${homeMaterials.length <= 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-6`}>
          {homeMaterials.map((mat) => (
            <Card key={mat.id} className="group border-0 shadow-sm hover:shadow-md transition-all overflow-hidden">
              <div className="aspect-[4/3] bg-secondary overflow-hidden flex items-center justify-center">
                {mat.image_url ? (
                  <img
                    src={cdnImage(mat.image_url, { width: 600, quality: 70 })}
                    alt={`${mat.name} countertop material`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                    width="400"
                    height="300"
                  />
                ) : (
                  <span className="text-muted-foreground font-display text-2xl">{mat.name}</span>
                )}
              </div>
              <CardContent className="p-5">
                <h3 className="font-display text-lg font-semibold">{mat.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{mat.description}</p>
                <Link to={`/materials/${mat.slug || mat.id}`} className="text-accent text-sm font-medium mt-3 inline-flex items-center gap-1 hover:gap-2 transition-all">
                  Explore <ArrowRight className="h-3 w-3" />
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Process */}
      <Section className="bg-secondary/50">
        <SectionHeader title="How It Works" subtitle="From selection to installation, we make it effortless" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {processSteps.map((step, i) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.4 }}
              onClick={() => handleStepClick(step.href)}
              className={`text-center cursor-pointer rounded-xl p-6 transition-all hover:shadow-lg hover:-translate-y-1 ${
                step.highlight
                  ? "bg-primary text-primary-foreground ring-2 ring-accent shadow-md"
                  : "bg-card"
              }`}
            >
              <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${
                step.highlight ? "bg-accent/20" : "bg-accent/10"
              }`}>
                <step.icon className="h-6 w-6 text-accent" />
              </div>
              <p className="text-xs text-accent font-semibold mb-1">Step {i + 1}</p>
              <h3 className="font-display text-lg font-semibold mb-2">{step.title}</h3>
              <p className={`text-sm mb-4 ${step.highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{step.desc}</p>
              <Button
                size="sm"
                className={step.highlight
                  ? "bg-accent text-accent-foreground hover:bg-accent/90 w-full"
                  : "bg-primary text-primary-foreground hover:bg-primary/90 w-full"
                }
                onClick={(e) => { e.stopPropagation(); handleStepClick(step.href); }}
              >
                {step.cta} <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Why Choose Altar Stone */}
      <Section>
        <SectionHeader title="Why Choose Altar Stone" subtitle="Your project deserves the best in craftsmanship and service" />
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-muted-foreground leading-relaxed">
            Altar Stone Countertops serves homeowners across Sarasota, Bradenton, Venice, Port Charlotte, Fort Myers, Naples and all of Southwest Florida. We specialize in premium quartz, marble, quartzite and granite countertop fabrication and installation. Our team brings precision craftsmanship to every kitchen and bathroom renovation project. Call <a href="tel:9415413348" className="text-accent font-medium underline underline-offset-2">(941) 541-3348</a> for your free estimate.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {co.licensedInsuredEnabled && (
            <div className="text-center p-6">
              <Shield className="h-10 w-10 text-accent mx-auto mb-4" />
              <h3 className="font-display text-lg font-semibold mb-2">{co.licensedInsuredText}</h3>
              <p className="text-sm text-muted-foreground">Fully licensed Florida contractor with comprehensive liability and workers' comp coverage.</p>
            </div>
          )}
          <div className="text-center p-6">
            <Star className="h-10 w-10 text-accent mx-auto mb-4" />
            <h3 className="font-display text-lg font-semibold mb-2">5-Star Reviews</h3>
            <p className="text-sm text-muted-foreground">Consistently rated 5 stars by homeowners throughout Southwest Florida.</p>
          </div>
          <div className="text-center p-6">
            <MapPin className="h-10 w-10 text-accent mx-auto mb-4" />
            <h3 className="font-display text-lg font-semibold mb-2">Local Experts</h3>
            <p className="text-sm text-muted-foreground">Proudly serving Sarasota, Bradenton, Venice, Fort Myers, Naples and all of Southwest Florida.</p>
          </div>
        </div>
      </Section>

      {/* Service Areas */}
      <Section className="bg-secondary/50">
        <SectionHeader title="Serving All of Southwest Florida" subtitle="Premium countertop installation from Tampa to Fort Myers" />
        <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
          {["Sarasota", "Bradenton", "Venice", "Englewood", "Port Charlotte", "Punta Gorda", "North Port", "Osprey", "Nokomis", "Lakewood Ranch", "Fort Myers", "Naples", "Bonita Springs", "Cape Coral", "Estero"].map((city) => (
            <span key={city} className="px-4 py-2 bg-card rounded-full text-sm font-medium text-foreground shadow-sm">
              {city}
            </span>
          ))}
        </div>
        <p className="text-center text-muted-foreground text-sm mt-6 max-w-xl mx-auto">
          Altar Stone provides expert countertop fabrication and installation services throughout Southwest Florida. Contact us at <a href="tel:9415413348" className="text-accent underline underline-offset-2">(941) 541-3348</a> to schedule your free consultation.
        </p>
      </Section>

      {/* Testimonials */}
      <Section className="bg-primary text-primary-foreground">
        <SectionHeader title="What Our Clients Say" subtitle="Real 5-star reviews from verified Google customers" className="[&_h2]:text-primary-foreground [&_p]:text-primary-foreground/85" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { text: "John did a quick and excellent job of installing my new quartz countertops in my kitchen. Highly recommended!", name: "slkelson", when: "2 weeks ago" },
            { text: "Called Sunday to set up job and Thursday they installed it. Installers were very professional and polite. I got quartz and it was cheaper than the laminate countertop I had with another company. Very pleased.", name: "David Eddings", when: "6 days ago" },
            { text: "They were very helpful throughout the entire process, answering all my questions and making sure I was completely satisfied. The installation was smooth, clean, and done right on schedule.", name: "Luiz Abib", when: "a month ago" },
            { text: "John was great, accurate quote, timely install.", name: "Chris Carlson", when: "a month ago" },
            
            { text: "Amazing quality and service. Our kitchen looks incredible!", name: "Sarah M.", when: "Sarasota FL" },
          ].map((review, i) => (
            <Card key={i} className="bg-primary-foreground/5 border-primary-foreground/10">
              <CardContent className="p-6">
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className="h-4 w-4 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-primary-foreground/95 text-sm italic">"{review.text}"</p>
                <p className="text-primary-foreground/80 text-xs mt-3">— {review.name} · {review.when}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="text-center mt-8">
          <a
            href="https://g.page/r/CS4mHXcewSFZEAE/review"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-accent hover:text-accent/80 text-sm font-medium underline underline-offset-4"
          >
            Read all reviews on Google ★
          </a>
        </div>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Altar Stone Countertops",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5.0",
            "bestRating": "5",
            "worstRating": "1",
            "ratingCount": "14",
            "reviewCount": "14"
          },
          "review": [
            { "@type": "Review", "author": { "@type": "Person", "name": "slkelson" }, "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }, "reviewBody": "John did a quick and excellent job of installing my new quartz countertops in my kitchen. Highly recommended!" },
            { "@type": "Review", "author": { "@type": "Person", "name": "David Eddings" }, "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }, "reviewBody": "Called Sunday to set up job and Thursday they installed it. Installers were very professional and polite. I got quartz and it was cheaper than the laminate countertop I had with another company. Very pleased." },
            { "@type": "Review", "author": { "@type": "Person", "name": "Luiz Abib" }, "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }, "reviewBody": "They were very helpful throughout the entire process, answering all my questions and making sure I was completely satisfied. The installation was smooth, clean, and done right on schedule." },
            { "@type": "Review", "author": { "@type": "Person", "name": "Chris Carlson" }, "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }, "reviewBody": "John was great, accurate quote, timely install." }
          ]
        }) }} />
      </Section>

      {/* FAQ */}
      <Section>
        <SectionHeader title="Frequently Asked Questions" subtitle="Everything you need to know about your countertop project" />
        <div className="max-w-2xl mx-auto">
          <Accordion type="single" collapsible>
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left font-display">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* Featured Projects */}
      {featuredProjects.length > 0 && (
        <Section>
          <SectionHeader title="Our Recent Work" subtitle="See our latest countertop installations across Southwest Florida" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProjects.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
              >
                <Link to={p.slug ? `/portfolio/${p.slug}` : "/portfolio"} className="block">
                  <Card className="group overflow-hidden border-0 shadow-sm hover:shadow-md transition-all cursor-pointer">
                    <div className="aspect-[4/3] bg-secondary overflow-hidden">
                      {p.thumb ? (
                        <img
                          src={p.thumb.image_url}
                          alt={p.thumb.alt_text || `${p.title} countertop installation`}
                          className="w-full h-full object-cover transition-transform duration-500"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <Image className="h-8 w-8" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-display font-semibold group-hover:text-accent transition-colors">{p.title}</h3>
                      <p className="text-sm text-muted-foreground">{p.material} · {p.category} · {p.city}</p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button asChild variant="outline" size="lg">
              <Link to="/portfolio">View All Projects <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
        </Section>
      )}

      {/* CTA */}
      <Section className="bg-accent/5">
        <div className="text-center max-w-xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl font-semibold mb-4">Ready to Get Started?</h2>
          <p className="text-muted-foreground mb-6">Get your estimated investment range in minutes — or schedule a free in-home consultation.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
              <Link to="/quote">Get Your Estimate</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/book">Book Consultation</Link>
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Index;
